/**
 * NonASNLoadTrend
 *
 * "Non-ASN Load % Trend" line chart for the ASN Gap Overview tab — Non-ASN
 * load percentage by the selected Chart Configuration → Timeframe grain
 * (Period / Week / Year), with a dashed configured-target line. Each chart
 * point shows its `formattedPercentage` directly above it and, stacked
 * immediately below that (still above the point marker), its `pointChange`
 * change label (green = IMPROVEMENT/decrease, red = DECLINE/increase, no
 * label when `status` is null) — there is no separate change summary rendered
 * below the chart.
 *
 * Data source: POST /api/asn/trend/non-asn-load-trend (fetched/owned by
 * AsnGapDashboard, same as the Overview KPI cards and Bottom Performing
 * Non-ASN table). This component only renders `trendPoints` exactly as
 * returned — it never adds/removes/reorders points, and never recomputes
 * `pointChange`/`status`/`formattedPercentage`, all of which are backend-
 * evaluated.
 *
 * Reuses the project's existing charting library (Victory / victory-native,
 * same conditional require pattern as `PTExecutionTrendChart`) rather than
 * introducing a new chart dependency. Built as a new component instead of
 * reusing `PTExecutionTrendChart` directly because the delta color logic is
 * inverted (PT Execution: higher is better, so its increment labels are
 * always green; here: lower is better, so decreases are green and increases
 * are red) and this chart additionally needs a dashed target reference line
 * that PT Execution's trend chart does not have — forcing reuse would have
 * meant conditionally branching most of that component's render logic.
 */

import React, { useMemo, useState } from 'react';
import { LayoutChangeEvent, Platform, Pressable, ScrollView, Text, View, useWindowDimensions } from 'react-native';
import { useTheme } from '../../../../../theme/useTheme';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import DLOTooltip, { DLOSimpleTooltip } from '../../../../../modules/dlo/components/DLOTooltip';
import { FontFamily } from '../../../../../constants/fonts';
import { asnOverviewChartsStyles as styles } from '../styles/asnOverviewChartsStyles';
import ChartSkeleton from '../../../components/ChartSkeleton';
import { formatPercentagePointDelta } from '../utils/asnLoadsComparisonAdapter';
import { ASNNonAsnLoadTrendPoint } from '../api/asnOverviewKpiApi';
import { OverviewTimeframeLevel } from './Overview';

let VictoryAxis: any,
  VictoryArea: any,
  VictoryChart: any,
  VictoryLine: any,
  VictoryScatter: any,
  VictoryLabel: any,
  VictoryTheme: any;

const assignVictoryModules = (pkg: any) => {
  ({ VictoryAxis, VictoryArea, VictoryChart, VictoryLine, VictoryScatter, VictoryLabel, VictoryTheme } = pkg);
};

if (Platform.OS === 'web') {
  assignVictoryModules(require('victory'));
} else {
  try {
    assignVictoryModules(require('victory-native'));
  } catch (error) {
    assignVictoryModules(require('victory'));
  }
}

export interface NonASNLoadTrendProps {
  /** POST /api/asn/trend/non-asn-load-trend `trendPoints` — rendered
   * exactly as returned (no added/removed/reordered points). */
  trendPoints?: ASNNonAsnLoadTrendPoint[];
  /** Response's `targetPercentage` — drives the dashed target line.
   * Undefined suppresses the target line entirely. */
  targetPercentage?: number;
  /** Chart Configuration -> Timeframe selection driving the title/subtitle
   * text ("by Period"/"by Week"/"by Year") and the X-axis interval display
   * formatting. Defaults to 'period' (the page-load default). */
  timeframeGrain?: OverviewTimeframeLevel;
  /** Response's `message` — shown instead of the chart when `trendPoints`
   * is empty (e.g. "Not enough periods in scope..."). */
  message?: string;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

// Base axis label style — fill is overridden per-render with a theme-aware color.
const axisLabelStyleBase = {
  fontSize: 12,
  lineHeight: 16,
  fontFamily: FontFamily.regular,
};

// pointLabelStyle and targetLabelStyle are NOT module-level constants —
// their fills must be theme-aware so they stay visible in light mode.
// They are derived inside the component (see below).

const CHANGE_LABEL_GREEN = '#0A8A1F';
const CHANGE_LABEL_RED = '#C12C01';
const CHANGE_LABEL_NEUTRAL = '#8A8F98';

// Wider than a single stacked label needs so the two-line (percentage +
// change) label at each point has room without colliding with its neighbors.
const MIN_POINT_SPACING = 72;
// Vertical gap (px) between the percentage label and the change label
// stacked directly beneath it, both positioned above the point marker.
const CHANGE_LABEL_GAP = 13;

const TIMEFRAME_TITLE_LABELS: Record<OverviewTimeframeLevel, string> = {
  period: 'Period',
  week: 'Week',
  year: 'Year',
};

/**
 * Formats a backend `interval` key (e.g. "01_2026" for week/period grains,
 * or a bare "2026" for the year grain) into an X-axis tick label + optional
 * year sub-label — applying only the existing display convention (P{n}/
 * W{n} + trailing 2-digit year) for the selected Timeframe. The underlying
 * interval value itself is never altered, supplemented, or recomputed.
 */
export const formatIntervalLabel = (
  interval: string,
  timeframeGrain: OverviewTimeframeLevel,
): { tickLabel: string; subLabel?: string } => {
  const parts = interval.split('_');
  if (parts.length === 2) {
    const [unit, year] = parts;
    const prefix = timeframeGrain === 'week' ? 'W' : timeframeGrain === 'period' ? 'P' : '';
    // Backend zero-pads the unit (e.g. "01") — strip that so it reads "P1"/
    // "W1" like every other period/week label in the app, not "P01"/"W01".
    const numericUnit = Number(unit);
    const unitLabel = Number.isFinite(numericUnit) ? String(numericUnit) : unit;
    return {
      tickLabel: prefix ? `${prefix}${unitLabel}` : unitLabel,
      subLabel: year ? `'${year.slice(-2)}` : undefined,
    };
  }
  // No underscore — e.g. a bare year interval ("2026") for the year grain.
  return { tickLabel: interval };
};

const MOBILE_BREAKPOINT = 620;

const NonASNLoadTrend: React.FC<NonASNLoadTrendProps> = ({
  trendPoints = [],
  targetPercentage,
  timeframeGrain = 'period',
  message,
  loading = false,
  error = null,
  onRetry,
}) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  // Light mode: use near-black so axis numbers are readable against the white
  // chart background. Dark mode: keep the existing muted gray (#777777).
  const axisTickFill = isDark ? '#777777' : '#1F2937';
  const axisSubTickFill = isDark ? '#9CA3AF' : '#4B5563';
  const axisLabelStyle = { ...axisLabelStyleBase, fill: axisTickFill };

  // Point percentage label — blue in both themes (visible on both dark and
  // light chart backgrounds). Target label — red in both themes. Both are
  // derived here (not as module-level constants) so the fill value is always
  // resolved against the current theme at render time.
  const pointLabelStyle = {
    fontSize: 11,
    lineHeight: 14,
    fill: '#0B5FC6',
    fontFamily: FontFamily.bold,
  };
  const targetLabelStyle = {
    fontSize: 11,
    lineHeight: 14,
    fill: '#C12C01',
    fontFamily: FontFamily.medium,
  };
  // VictoryTheme.material defaults to white label fills which makes text
  // invisible on the light background.  Override the base label color so
  // every Victory child that doesn't set its own explicit fill inherits a
  // readable value.
  const victoryTheme = {
    ...VictoryTheme.material,
    axis: {
      ...VictoryTheme.material?.axis,
      style: {
        ...VictoryTheme.material?.axis?.style,
        tickLabels: {
          ...VictoryTheme.material?.axis?.style?.tickLabels,
          fill: axisTickFill,
        },
      },
    },
  };

  const isIOS = Platform.OS === 'ios';
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = windowWidth < MOBILE_BREAKPOINT;
  const [containerWidth, setContainerWidth] = useState(0);
  const axisLeftOffset = isIOS ? 44 : 54;
  const hasTarget = typeof targetPercentage === 'number' && Number.isFinite(targetPercentage);

  // Render exactly what the backend returned — one chart point per
  // trendPoint, in the order given, with no added/removed/reordered points.
  // Each point carries both of its labels (percentage + change) so they can
  // be rendered together, stacked at the same point, instead of the change
  // value being rendered separately below the chart.
  const chartData = useMemo(
    () =>
      trendPoints.map((point, index) => {
        const { tickLabel, subLabel } = formatIntervalLabel(point.interval, timeframeGrain);
        // Alternate the percentage label's distance from its point so
        // adjacent points' stacked (percentage + change) label pairs don't
        // collide horizontally.
        const percentLabelDy = index % 2 === 0 ? -18 : -34;
        const hasChange = point.status !== null && point.pointChange !== null;
        return {
          x: index,
          y: point.percentNonAsnLoads,
          tickLabel,
          subLabel,
          displayPercentage: point.formattedPercentage,
          percentLabelDy,
          changeLabelDy: percentLabelDy + CHANGE_LABEL_GAP,
          changeLabelText: hasChange ? formatPercentagePointDelta(point.pointChange as number) : '',
          // Lower is better: negative pointChange (decrease) → green,
          // positive pointChange (increase) → red, exactly zero → neutral.
          changeLabelColor: !hasChange || (point.pointChange as number) === 0
            ? CHANGE_LABEL_NEUTRAL
            : (point.pointChange as number) < 0
              ? CHANGE_LABEL_GREEN
              : CHANGE_LABEL_RED,
        };
      }),
    [trendPoints, timeframeGrain],
  );

  const hasSubLabels = useMemo(() => chartData.some((d) => d.subLabel != null), [chartData]);

  const maxY = useMemo(() => {
    const maxDataY = Math.max(0, ...chartData.map((d) => d.y));
    const ceilingSource = hasTarget ? Math.max(maxDataY, targetPercentage as number) : maxDataY;
    return Math.max(20, Math.ceil((ceilingSource + 8) / 5) * 5);
  }, [chartData, hasTarget, targetPercentage]);

  const yTicks = useMemo(() => {
    const step = Math.ceil(maxY / 4);
    const raw = [0, step, step * 2, step * 3, maxY];
    return [...new Set(raw)].filter((v) => v <= maxY);
  }, [maxY]);

  const indexTicks = useMemo(() => chartData.map((item) => item.x), [chartData]);

  const chartWidth = useMemo(() => {
    const minFromPoints = Math.max(280, trendPoints.length * MIN_POINT_SPACING);
    if (containerWidth <= 0) return minFromPoints;
    return Math.max(containerWidth - 8, minFromPoints);
  }, [containerWidth, trendPoints.length]);

  const needsHScroll = containerWidth > 0 && chartWidth > containerWidth - 8;

  const onLayoutContainer = (event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  };

  const renderBody = () => {
    if (loading) {
      return <ChartSkeleton height={240} />;
    }
    if (error) {
      return (
        <View style={styles.emptyContainer}>
          <Text style={styles.errorText}>{error}</Text>
          {onRetry && (
            <Pressable style={styles.retryButton} onPress={onRetry} accessibilityRole="button" accessibilityLabel="Retry">
              <Text style={styles.retryButtonText}>Retry</Text>
            </Pressable>
          )}
        </View>
      );
    }
    if (chartData.length === 0) {
      return (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>{message || 'No data available'}</Text>
        </View>
      );
    }

    return (
      <View style={styles.trendChartContainer} onLayout={onLayoutContainer}>
        <ScrollView
          horizontal
          scrollEnabled={needsHScroll}
          showsHorizontalScrollIndicator={needsHScroll}
          contentContainerStyle={{ flexGrow: 0 }}
          scrollEventThrottle={16}
        >
          <VictoryChart
            width={chartWidth}
            height={isIOS ? 220 : 260}
            domain={{
              x: [-0.5, Math.max(0, chartData.length - 1) + 0.5],
              y: [0, maxY],
            }}
            padding={
              isIOS
                ? { top: 44, right: 22, bottom: hasSubLabels ? 36 : 24, left: 44 }
                : { top: 50, right: 60, bottom: hasSubLabels ? 54 : 42, left: 54 }
            }
            theme={victoryTheme}
          >
            <VictoryAxis
              dependentAxis
              orientation="left"
              crossAxis={false}
              offsetX={axisLeftOffset}
              tickValues={yTicks}
              tickFormat={(tick: number) => `${tick}%`}
              style={{
                axis: { stroke: '#D8D8D8', strokeWidth: 1 },
                ticks: { stroke: 'transparent', size: 0 },
                tickLabels: axisLabelStyle,
                grid: { stroke: '#ECEFF4', strokeWidth: 1, strokeDasharray: '6,6' },
              }}
            />

            {/* X-axis interval labels — kept below the chart as before */}
            <VictoryAxis
              tickValues={indexTicks}
              tickLabelComponent={
                <VictoryLabel
                  text={({ datum }: any) => {
                    const pt = chartData[datum];
                    if (pt?.subLabel) return [pt.tickLabel, pt.subLabel];
                    return [pt?.tickLabel ?? String(datum)];
                  }}
                  style={[
                    { fontSize: 12, fill: axisTickFill, fontFamily: FontFamily.regular },
                    { fontSize: 10, fill: axisSubTickFill, fontFamily: FontFamily.regular },
                  ]}
                  lineHeight={1.4}
                />
              }
              style={{
                axis: { stroke: '#D8D8D8', strokeWidth: 1 },
                ticks: { stroke: 'transparent', size: 0 },
                grid: { stroke: 'transparent' },
              }}
            />

            <VictoryArea
              data={chartData}
              interpolation="monotoneX"
              style={{ data: { fill: '#D9DEE7', fillOpacity: 0.6, stroke: 'transparent' } }}
            />

            {/* Dashed configured-target reference line, with a label near it */}
            {hasTarget && (
              <VictoryLine
                data={[
                  { x: -0.5, y: targetPercentage as number },
                  { x: Math.max(0, chartData.length - 1) + 0.5, y: targetPercentage as number },
                ]}
                style={{ data: { stroke: '#C12C01', strokeWidth: 1.5, strokeDasharray: '6,4', opacity: 0.5 } }}
              />
            )}
            {hasTarget && (
              <VictoryScatter
                data={[{ x: Math.max(0, chartData.length - 1) + 0.5, y: targetPercentage as number }]}
                size={0}
                labels={[`Target ${(targetPercentage as number).toFixed(1)}%`]}
                style={{ labels: targetLabelStyle }}
                labelComponent={<VictoryLabel dx={-6} dy={-8} textAnchor="end" style={targetLabelStyle} />}
              />
            )}

            <VictoryLine
              data={chartData}
              interpolation="monotoneX"
              style={{ data: { stroke: '#0B5FC6', strokeWidth: 3 } }}
            />

            {/* formattedPercentage — rendered directly above each point */}
            <VictoryScatter
              data={chartData}
              size={6.5}
              style={{ data: { fill: '#0B5FC6', stroke: '#0B5FC6', strokeWidth: 2 }, labels: pointLabelStyle }}
              labels={({ datum }: any) => datum.displayPercentage}
              labelComponent={
                <VictoryLabel dy={({ datum }: any) => datum.percentLabelDy} style={pointLabelStyle} />
              }
            />

            {/* pointChange — stacked directly below the percentage label,
                still above the point marker; empty string renders nothing
                for points whose status is null (e.g. the first point).
                NOTE: `fill` must be a function on an individual style
                property (Victory's documented "functional styles" pattern)
                — passing the whole `style` object as a single function is
                NOT reliably evaluated per-datum by Victory/victory-native
                and was the root cause of the color not updating per point. */}
            <VictoryScatter
              data={chartData}
              size={0}
              labels={({ datum }: any) => datum.changeLabelText}
              style={{ labels: { fill: ({ datum }: any) => datum.changeLabelColor } }}
              labelComponent={
                <VictoryLabel
                  dy={({ datum }: any) => datum.changeLabelDy}
                  style={{
                    fontSize: 9,
                    lineHeight: 11,
                    fontFamily: FontFamily.bold,
                    fill: ({ datum }: any) => datum.changeLabelColor,
                  }}
                />
              }
            />
          </VictoryChart>
        </ScrollView>
      </View>
    );
  };

  return (
    <View style={styles.card} testID="nonasn-load-trend-card">
      {isMobile ? (
        /* ── Mobile header: title on first line; subtitle + info icon inline on second ── */
        <View style={styles.mobileHeaderRow}>
          <View style={styles.mobileHeaderLeft}>
            <Text style={styles.title} numberOfLines={1}>Non-ASN Load % Trend</Text>
            <View style={styles.mobileSubtitleRow}>
              <Text style={styles.subTitle} numberOfLines={1}>by {TIMEFRAME_TITLE_LABELS[timeframeGrain]}</Text>
              {/* Instance-specific tooltip: positioned directly above the info
                  icon on mobile. verticalOffset nudges it clear of the icon;
                  maxWidth keeps it within narrow viewports. */}
              <DLOTooltip
                content="Lower percentages are better. Decreases appear in green, and the dashed line represents the configured target."
                position="top"
                trigger="click"
                verticalOffset={-6}
                maxWidth={220}
              >
                <View style={styles.infoIconWrap}>
                  <MaterialIcons name="info-outline" size={16} color="#8A8F98" />
                </View>
              </DLOTooltip>
            </View>
          </View>
        </View>
      ) : (
        /* ── Web/tablet header: existing single-row layout ── */
        <>
          <View style={styles.headerRow}>
            <View style={styles.headerTextGroup}>
              <Text style={styles.title}>Non-ASN Load % Trend</Text>
              <Text style={styles.subTitle}>by {TIMEFRAME_TITLE_LABELS[timeframeGrain]}</Text>
            </View>
          </View>
          <Text style={styles.description}>
            Lower is better. Decreases render green. Dashed line is the configured target.
          </Text>
        </>
      )}

      {renderBody()}
    </View>
  );
};


export default NonASNLoadTrend;
